import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'
import { Box, Breadcrumbs, CircularProgress, Typography } from '@mui/material'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import z from 'zod'
import { useVouchersNoMealReport } from '@/shared/api/vouchers-no-meal-report'
import { PERMISSION_SUB_MODULES } from '@/shared/const'
import { CustomButton } from '@/shared/ui/button'
import { NotFound } from '@/shared/ui/not-found'
import { PageWrapper } from '@/shared/ui/page-wrapper'
import { PermissionGuard } from '@/shared/ui/permission-guard'
import { MissedMealsWidget } from '@/widgets/missed-meals-widget'

const searchSchema = z.object({
  'pagination.page': z.number().optional(),
  'pagination.pageSize': z.number().optional(),
  'filter.search': z.string().optional(),
  'filter.schoolId': z.string().optional(),
  'filter.grades': z.array(z.number()).optional(),
  'filter.classLetters': z.array(z.string()).optional(),
  'filter.dateFrom': z.string().optional(),
  'filter.dateTo': z.string().optional(),
})

export const Route = createFileRoute('/_auth/reports/no-meal-vouchers_/item/$reportId')({
  staticData: {
    meta: {
      title: 'Список учеников не получивших питание',
      description: '',
    },
  },
  validateSearch: searchSchema,
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <PermissionGuard permission={PERMISSION_SUB_MODULES.VOUCHER_REPORT_SCHOOL}>
      <ReportContent />
    </PermissionGuard>
  )
}

function ReportContent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { reportId } = Route.useParams()

  const { data: report, isLoading: isReportLoading, isError } = useVouchersNoMealReport(reportId)

  const handleNotFound = useCallback(
    (message: string) => {
      toast.error(message || t('За выбранный период данных не найдено'))
      navigate({ to: '/reports/no-meal-vouchers', replace: true })
    },
    [navigate, t]
  )

  const handleBack = () => {
    navigate({ to: '/reports/no-meal-vouchers' })
  }

  if (isReportLoading) {
    return (
      <PageWrapper title={t('Список учеников не получивших питание')}>
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      </PageWrapper>
    )
  }

  if (isError || !report) {
    return <NotFound title={t('Ошибка')} description={t('Не удалось загрузить данные отчета')} />
  }

  const period = `${dayjs(report.dateFrom).format('DD.MM.YYYY')} - ${dayjs(report.dateTo).format('DD.MM.YYYY')}`

  return (
    <PageWrapper title={t('Список учеников не получивших питание')}>
      <Box display="flex" gap={2} alignItems="center">
        <CustomButton
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIos />}
          onClick={handleBack}
        >
          {t('Назад')}
        </CustomButton>
        <Breadcrumbs separator={<ArrowForwardIos sx={{ fontSize: 16 }} />}>
          <Typography color="text.secondary">
            {t('Список учеников не получивших питание за период')}
          </Typography>
          <Typography color="text.primary">{period}</Typography>
        </Breadcrumbs>
      </Box>
      <MissedMealsWidget
        dateFrom={report.dateFrom}
        dateTo={report.dateTo}
        schoolIds={report.schoolIds}
        grades={report.grades}
        classLetters={report.classLetters}
        onNotFound={handleNotFound}
      />
    </PageWrapper>
  )
}
